<template>
    <!--
        Toolbox component.
        Displays a toolbox overlay
        Closable by clicking button close (x) or by finishing the process
    -->
    <transition name="fade">
        <div ref="toolbox" :class="[
            'toolbox',
            'toolbox--' + toolboxClass, show ? 'show' : '',
            ready ? '' : 'positioning',
            hasInput ? 'toolbox--has-input' : '',
            os === 'ios' ? 'ios' : '',
            tiny && hasInput ? 'toolbox--tiny' : ''
        ]" :style="coordinates" @click.stop>
            <span class="toolbox-arrow" :class="posArrow" :style="arrowCoordinates" v-if=" ! (tiny && hasInput)" ></span>
            <component :is="type" :payload="payload" :replacement="replacement"  v-if=" ! (tiny && hasInput)" />
            <div class="toolbox-tiny" v-else>
                <p>{{ $t('common.device.rotate') }}</p>
                <action color="blue" @click.native="close">OK</action>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex';
import events from '../../events/events';

export default
{
    name: 'Toolbox',

    data() {
        return {
            show: false,
            coordinates: {},
            arrowCoordinates: {},
            ready: false,
            posArrow: '',
            type: '',
            toolboxClass: '',
            payload: null,
            replacement: false, // is the payload being replaced?
            hasInput: false,
        }
    },

    created()
    {
        events.$on('input.focus', focus => { this.hasInput = focus; });

        events.$listen('toolbox.payload', payload =>
        {
            this.payload = payload;
            this.replacement = true;
        });

        events.$listen('toolbox.open', (type, event, payload) =>
        {
            this.replacement = false;

            if (type == 'tools')
            {
                let different = false;
                if (payload == null && this.payload != null)
                {
                    different = true;
                }
                else if (payload != null && this.payload == null)
                {
                    different = true;
                }
                else if (typeof payload == 'object' && typeof this.payload == 'object')
                {
                    different = payload.id != this.payload.id;
                }
                else if (typeof payload == 'string' && typeof this.payload == 'string')
                {
                    different = payload != this.payload;
                }
                else
                {
                    different = true;
                }

                if (type != this.type || ! this.payload || different)
                {
                    this.type = type;
                    this.toolboxClass = (this.$onduro.hasRole('planner')) ? 'tools' : 'tools-user';
                    this.open(event);
                }
                else
                {
                    this.close();
                    return;
                }
            }

            else if (type == 'timepicker')
            {
                if (type != this.type || ! this.payload || JSON.stringify(payload) != JSON.stringify(this.payload))
                {
                    this.type = type;
                    this.toolboxClass = type;
                    this.open(event);
                }
                else
                {
                    this.close();
                    return;
                }
            }

            else if (type == 'calendar')
            {
                if (type != this.type || ! this.payload)
                {
                    this.type = type;
                    this.toolboxClass = type;
                    this.open(event, 'top');
                }
                else
                {
                    this.close();
                    return;
                }
            }

            else if (type == 'notes')
            {
                if (type != this.type || ! this.payload || payload != this.payload)
                {
                    this.type = type;
                    this.toolboxClass = type;
                    this.open(event);
                }
                else
                {
                    this.close();
                    return;
                }
            }

            else if (type == 'chatbox' || type == 'itemchatbox')
            {
                if (type != this.type || ! this.payload || payload != this.payload)
                {
                    this.type = type;
                    this.toolboxClass = type;
                    this.open(event);
                }
                else
                {
                    this.close();
                    return;
                }
            }

            this.payload = payload;
        });

        events.$listen('toolbox.close', () =>
        {
            if (this.show) this.close();
        });
    },

    computed:
    {
        ...mapState({
            viewport: ({ layout }) => layout.viewport,
            os: ({ app }) => app.os
        }),

        tiny()
        {
            return this.viewport !== 'd' && this.viewport !== 'tl' && window.innerHeight < 500;
        }
    },

    methods:
    {
        open(event, position)
        {
            event.stopPropagation();
            this.reset();

            this.setPosition(event, position);

            this.show = true;

            let containerContent = document.querySelector('.container .content');
            let containerContentMain = document.querySelector('.container .content-main');

            // keyboard will trigger resize, so chatbox will be closed immediately
            if (this.type != 'chatbox' && this.type != 'itemchatbox' || ! this.$store.state.app.touchDevice)
            {
                if (containerContent) containerContent.addEventListener('scroll', this.close);
                if (containerContentMain) containerContentMain.addEventListener('scroll', this.close);

                window.addEventListener('resize', this.resizeCLose);
            }

            // do not allow to close tools on click
            if (this.type != 'tools') window.addEventListener('click', this.close);
        },

        reset()
        {
            let containerContent = document.querySelector('.container .content');
            let containerContentMain = document.querySelector('.container .content-main');

            if (this.type != 'chatbox' && this.type != 'itemchatbox' || ! this.$store.state.app.touchDevice)
            {
                if (containerContent) containerContent.removeEventListener('scroll', this.close);
                if (containerContentMain) containerContentMain.removeEventListener('scroll', this.close);

                window.removeEventListener('resize', this.close);
            }

            if (this.type != 'tools') window.removeEventListener('click', this.close);

            this.coordinates = {};
            this.arrowCoordinates = {};
            this.posArrow = '';
            this.ready = false;
            this.show = false;
        },

        close()
        {
            this.hasInput = false;

            if (this.type == 'chatbox' || this.type == 'itemchatbox') events.$emit('chatbox.closed');

            this.reset();
            this.payload = null;
            this.type = '';
        },

        resizeClose()
        {
            if ( ! this.hasInput) this.close();
        },

        setPosition(event, position)
        {
            if (typeof this.$refs.toolbox === 'undefined') return;

            let body = document.querySelector('body'),
                offset = this.getOffset(event.currentTarget),
                scroll = this.getScroll(event.currentTarget),
                itemHeight = event.currentTarget.offsetHeight,
                itemWidth = event.currentTarget.offsetWidth,
                arrowHeight = 10;

            let coordinates = {
                top: offset.y + scroll.y + Math.round(itemHeight / 2) + 'px',
                left: offset.x + scroll.x + Math.round(itemWidth / 2) + 'px',
            };

            this.$set(this.coordinates, 'top', coordinates.top);
            this.$set(this.coordinates, 'left', coordinates.left);

            this.$nextTick(() =>
            {
                let toolboxHeight = this.$refs.toolbox.offsetHeight,
                    toolboxWidth = this.$refs.toolbox.offsetWidth,
                    bounds = this.$refs.toolbox.getBoundingClientRect(),
                    transX = 'translateX(-' + scroll.x + 'px)',
                    transY = 'translateY(-' + scroll.y + 'px)',
                    posX = 0, // always position in center
                    posY = ((itemHeight / 2) + (toolboxHeight / 2) + arrowHeight) + scroll.y,
                    top = bounds.top - posY,
                    bottom = (body.clientHeight + scroll.y) - (bounds.bottom + posY);

                // check Y (vertical)
                if (top > 0)
                {
                    transY = 'translateY(-' + Math.round(posY) + 'px)';
                    this.posArrow = 'is-bottom';
                }
                else if ((bottom + scroll.y) > 0)
                {
                    transY = 'translateY(' + Math.round(posY - scroll.y * 2) + 'px)';
                    this.posArrow = 'is-top';
                }
                else // if (bounds.top < 0 || body.clientHeight - bounds.bottom < 0)
                {
                    this.coordinates.top = 0;
                    transY = 'translateY(50%)';
                }

                // console.info(
                //     'bounds.left', bounds.left,
                //     'bounds.right', bounds.right,
                //     'body.clientWidth', body.clientWidth,
                //     'scroll.x', scroll.x,
                //     'body.clientWidth + scroll.x', body.clientWidth + scroll.x
                // );

                // if bounds over the right side
                if (bounds.right > (body.clientWidth + scroll.x))
                {
                    // console.info('RIGHT');
                    posX = bounds.right - body.clientWidth;

                    transX = 'translateX(' + Math.round(posX * -1) + 'px)';

                    let arrowX = Math.round(bounds.right - (body.clientWidth + scroll.x));

                    if (arrowX - itemWidth >= -10)
                    {
                        arrowX -= (itemWidth / 2) - 10;
                    }

                    this.arrowCoordinates = {
                        transform: 'translateX(-50%) translateX(' + arrowX + 'px)',
                    };
                }

                // if bounds over the left side
                else if (bounds.left < scroll.x)
                {
                    // console.info('LEFT');
                    posX = bounds.left;

                    transX = 'translateX(' + Math.round(posX * -1) + 'px)';

                    let arrowX = Math.round(bounds.left - scroll.x);

                    if (arrowX + itemWidth <= 10)
                    {
                        arrowX += (itemWidth / 2) - 10;
                    }

                    this.arrowCoordinates = {
                        transform: 'translateX(-50%) translateX(' + arrowX + 'px)',
                    };
                }

                if (position == 'top')
                {
                    this.posArrow = 'is-top';
                    this.$set(this.coordinates, 'transform', 'translate(-50%, 0) translateY(20px) ' + transX);
                }
                else
                {
                    this.$set(this.coordinates, 'transform', 'translate(-50%, -50%) ' + transY + ' ' + transX);
                }

                this.ready = true;
            });
        },

        getOffset(el)
        {
            el = el.getBoundingClientRect();

            let xPos = 0,
                yPos = 0;

            return {
                x: el.left,
                y: el.top
            };
        },

        getScroll(el)
        {
            let node = el,
                scrollX = 0,
                scrollY = 0;

            while (node.parentNode)
            {
                node = node.parentNode;

                scrollX += node.scrollLeft || 0;
                scrollY += node.scrollTop || 0;
            }

            return {
                x: Math.round(scrollX),
                y: Math.round(scrollY)
            };
        }
    },

    components:
    {
        'tools': require('./Tools'),
        'notes': require('./Notes'),
        'calendar': require('./Calendar'),
        'timepicker': require('./Timepicker'),
        'chatbox': require('./Chatbox'),
        'itemchatbox': require('./ItemChatbox'),
    }
}
</script>



// WEBPACK FOOTER //
// Toolbox.vue?67fc083e